import consumer from "./consumer"
import { titleize } from "../lib/text"
import { buildBoltPoints, drawBoltOnCtx, LIGHTNING } from "../lib/lightning"

const TEXT_VARIANT_CLASSES = ['text-success', 'text-info', 'text-warning', 'text-error']

const EFFICIENCY_STATUS_LABELS = {
  info: 'Deficit',
  success: 'Normal',
  warning: 'Decay',
  error: 'Penalty'
}

const gameUpdatesChannel = consumer.subscriptions.create("GameUpdatesChannel", {
  connected() {
    if (typeof this.gridStateLoaded === 'undefined') {
      this.resetGridStateTracking()
    }

    this.reconnected = true
    this.clearGridStateRetry()
    this.deadTiles = new Set()
    this._pendingLightning = []

    const gridFrame = this.gridFrameElement()
    if (!gridFrame) {
      this.gridStateLoaded = true
      this.pendingGridStateRefresh = false
      return
    }

    if (!this.gridStateLoaded && gridFrame.querySelector('.grid')) {
      this.handleGridStateLoaded()
    }

    // Initialize page title with current queue status
    this.initializePageTitleFromDOM()

    const forceFetch = !this.gridStateLoaded
    this.ensureGridState({ reason: 'connected', force: forceFetch })
  },

  disconnected() {
    this.reconnected = false
    this.pendingGridStateRefresh = true
    this._pendingLightning?.forEach(({ canvas, timers }) => {
      timers.forEach(clearTimeout)
      canvas.remove()
    })
    this._pendingLightning = []
  },

  received(data) {
    try {
      // Ignore Turbo stream messages (they're handled by Turbo, not us)
      if (typeof data === 'string' || (data.action && data.target)) {
        return
      }

      if (!this.gridStateLoaded && this.shouldDeferMessage(data)) {
        this.deferDomMessage(data)
        this.ensureGridState({ reason: 'message_deferred' })
        return
      }

      this.dispatchDomMessage(data)
    } catch (error) {
      console.error("[GameUpdatesChannel] Error processing message:", error, data)
      // Continue processing instead of breaking the channel
      this.handleProcessingError(error, data)
    }
  },

  dispatchDomMessage(data) {
    try {
      window.dispatchEvent(new CustomEvent('deepco:game_update', { detail: data }))
    } catch {
      // Ignore CustomEvent failures (e.g., older browsers)
    }

    switch (data.type) {
      case 'damage_batch':
        this.processDamageBatch(data)
        break
      case 'fork_batch':
        this.processForkBatch(data)
        break
      case 'worker_state':
        this.processWorkerState(data)
        break
      case 'queue_status':
        this.updateQueueStatus(data.queue.queued_count, data.queue.max_queue_size)
        break
      case 'tile_assignment':
        this.processTileAssignment(data)
        break
      case 'personal_queue_status':
        this.processPersonalQueueStatus(data)
        break
      case 'personal_queue_status_batch':
        this.processPersonalQueueStatusBatch(data)
        break
      case 'tile_cleanup':
        this.processTileCleanup(data)
        break
      case 'auto_claim_result':
        this.processAutoClaimResult(data)
        break
      case 'auto_upgrade_result':
        this.processAutoUpgradeResult(data)
        break
      case 'idle_started':
        this.handleIdleStarted(data)
        break
      default:
    }
  },

  processDamageBatch(data) {
    const { tile_id, damages, tile_health, tile_max_health, tile_dug, visual_effects } = data
    
    // Spawn damage floaters
    const container = document.getElementById(`damage-floaters-${tile_id}`)
    if (container) {
      damages.forEach(damageData => {
        this.spawnDamageFloater(container, damageData)
      })
    }
    
    // Update tile health progress bar
    this.updateTileHealth(tile_id, tile_health, tile_max_health, tile_dug)
    
    // Apply visual effects (chain, crit)
    if (visual_effects) {
      this.applyVisualEffects(tile_id, visual_effects)
    }
  },

  processForkBatch(data) {
    const { path_tile_ids, attacker_color, tile_updates } = data

    if (Array.isArray(path_tile_ids) && path_tile_ids.length > 0) {
      this.renderForkPath(path_tile_ids, attacker_color)
    }

    if (Array.isArray(tile_updates) && tile_updates.length > 0) {
      tile_updates.forEach(update => {
        this.processDamageBatch(update)
      })
    }
  },

  renderForkPath(tileIds, attacker_color) {
    if (document.body.dataset.strikeAnimationsEnabled !== 'true') return

    tileIds.forEach(tile_id => {
      const tileElement = document.querySelector(`#tile_inner_${tile_id} .tile`)
      if (!tileElement) return

      tileElement.classList.remove('chain-zap')
      tileElement.style.setProperty('--chain-color', attacker_color || 'rgba(0, 200, 255, 0.8)')
      tileElement.classList.add('chain-zap')
    })
  },

  processTileAssignment(data) {
    const { tile_id, active_workers } = data
    

    // Update active worker spinners (shared with all workers in department)
    this.updateActiveWorkerSpinners(tile_id, active_workers || [])
    
    // Update "you-are-mining-here" indicator
    this.updateMiningHereIndicator(tile_id, active_workers || [])
  },

  processPersonalQueueStatus(data) {
    const { tile_id, queued } = data
    

    // Update personal queue indicator for this worker only
    this.updatePersonalQueueIndicator(tile_id, queued)
  },

  processPersonalQueueStatusBatch(data) {
    const { tile_ids, queued } = data

    // Update personal queue indicators for all tiles in the batch
    tile_ids.forEach(tile_id => {
      this.updatePersonalQueueIndicator(tile_id, queued)
    })

    // Update queue counter after batch processing
    this.updateQueueCounterFromDOM()
  },

  processTileCleanup(data) {
    this.applyTileCleanup(data)
    this.cleanOrphanedSpinners(data.tile_id)
  },

  updateActiveWorkerSpinners(tile_id, active_workers) {
    const spinnerContainer = document.getElementById(`spinner_layer_${tile_id}`)
    if (!spinnerContainer) {
      return
    }
    
    // Clear existing spinners
    spinnerContainer.innerHTML = ''
    
    // Create new spinners for active workers
    active_workers.forEach((worker, index) => {
      const spinner = this.createWorkerSpinner(tile_id, worker, index)
      spinnerContainer.appendChild(spinner)
    })
  },

  createWorkerSpinner(tile_id, worker, index) {
    const spinner = document.createElement('div')
    spinner.id = `spinner-${tile_id}-${worker.id}`
    spinner.setAttribute('data-worker-id', worker.id)
    spinner.className = `mining-spinner ${worker.gradient_css_class || ''}`
    let borderStyle = `border-color: ${worker.effective_process_color}; border-top-color: transparent;'`

    spinner.style.cssText = `
      --spinner-speed: ${worker.dig_cooldown}s;
      --spinner-offset: ${index * 2}px;
      --spinner-delay: ${index * 0.2}s;
      ${borderStyle}
      z-index: ${3 - index};
    `
    return spinner
  },

  updateMiningHereIndicator(tile_id, active_workers) {
    // Find the tile inner element
    const tileInner = document.getElementById(`tile_inner_${tile_id}`)
    if (!tileInner) {
      return
    }
    
    // Get current worker ID from the page
    const currentWorkerId = parseInt(document.body.dataset.currentWorkerId, 10)
    
    // Check if current worker is in the active workers list
    const isCurrentWorkerMining = active_workers.some(worker => worker.id === currentWorkerId)
    
    // Add or remove the 'you-are-mining-here' class
    if (isCurrentWorkerMining) {
      tileInner.classList.add('you-are-mining-here')
      // Also remove any lingering queue indicator since we're actively mining
      tileInner.classList.remove('you-are-queued-here')
    } else {
      tileInner.classList.remove('you-are-mining-here')
      // In non-queue mode, also clear any lingering queue indicators
      // when we're no longer mining this tile
      if (!this.isQueueModeEnabled()) {
        tileInner.classList.remove('you-are-queued-here')
      }
    }
  },

  updatePersonalQueueIndicator(tile_id, is_queued) {
    // Find the tile inner element
    const tileInner = document.getElementById(`tile_inner_${tile_id}`)
    if (!tileInner) {
      return
    }
    
    // Add or remove the 'you-are-queued-here' class
    if (is_queued) {
      tileInner.classList.add('you-are-queued-here')
    } else {
      tileInner.classList.remove('you-are-queued-here')
    }
  },
  
  updateTileHealth(tile_id, health, max_health, dug) {
    const progressBar = document.querySelector(`#tile_inner_${tile_id} .progress-bar`)
    
    if (!progressBar) return
    
    if (dug) {
      // Hide progress bar when tile is completed
      progressBar.style.width = '0%'
      progressBar.style.display = 'none'
      // Tile is dead, trigger cleanup
      this.handleTileDeath(tile_id)
    } else {
      // Update progress bar width based on remaining health
      const progress = 100 - (health / max_health * 100)
      const progressPercent = Math.max(0, progress)
      progressBar.style.width = `${progressPercent}%`
      progressBar.style.display = 'block'
      
      // Check if progress bar is at 100% (tile is killed)
      if (progressPercent >= 100) {
        this.handleTileDeath(tile_id)
      }
    }
  },

  applyVisualEffects(tile_id, effects) {
    const { attacker_color, worker_id, crit, chain } = effects
    const tileElement = document.querySelector(`#tile_inner_${tile_id} .tile`)

    if (!tileElement) {
      return
    }

    // Apply chain effect
    if (chain) {
      tileElement.classList.remove('chain-zap') // Remove first in case it's already there
      tileElement.style.setProperty('--chain-color', attacker_color || 'rgba(0, 200, 255, 0.8)')
      tileElement.classList.add('chain-zap')
    }

    // Apply crit flash effect (only if strike animations are enabled)
    if (crit && document.body.dataset.strikeAnimationsEnabled === 'true') {
      this.triggerCritFlash(tile_id, worker_id, attacker_color)
    }
  },

  triggerCritFlash(tile_id, worker_id, attacker_color) {
    if (worker_id) {
      this.animateCritSpinner(tile_id, worker_id)
    }
    this.applyTileGlow(tile_id, attacker_color)
    this.drawLightningStrike(tile_id, attacker_color)
  },

  applyTileGlow(tile_id, attacker_color) {
    const tileElement = document.querySelector(`#tile_inner_${tile_id} .tile`)
    if (!tileElement) return
    tileElement.classList.remove('crit-zap')
    tileElement.style.setProperty('--crit-color', attacker_color || LIGHTNING.DEFAULT_COLOR)
    void tileElement.offsetWidth // force reflow so re-adding the class restarts the CSS animation
    tileElement.classList.add('crit-zap')
  },

  drawLightningStrike(tile_id, attacker_color) {
    const tileEl = document.querySelector(`#tile_inner_${tile_id}`)
    if (!tileEl) return
    const tileRect = tileEl.getBoundingClientRect()
    if (tileRect.width === 0) return

    const dpr = window.devicePixelRatio || 1
    const w = window.innerWidth, h = window.innerHeight
    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(w * dpr)
    canvas.height = Math.ceil(h * dpr)
    canvas.style.cssText = `position:fixed;top:0;left:0;width:${w}px;height:${h}px;pointer-events:none;z-index:1;`
    document.body.appendChild(canvas)
    const ctx = canvas.getContext('2d')
    if (!ctx) { canvas.remove(); return }
    ctx.scale(dpr, dpr)

    const endX = tileRect.left + tileRect.width / 2
    const endY = tileRect.top + tileRect.height / 2
    const startX = Math.random() * w
    const color = attacker_color || LIGHTNING.DEFAULT_COLOR
    const roughness = tileRect.width * LIGHTNING.ROUGHNESS_RATIO

    const points = buildBoltPoints(startX, LIGHTNING.START_Y, endX, endY, roughness, roughness * LIGHTNING.MIN_LEN_RATIO)
    const drawPass = (opacity) => {
      // Use logical (unscaled) dimensions for clearRect because ctx.scale(dpr) is already applied,
      // which means coordinates are automatically scaled by the device pixel ratio.
      ctx.clearRect(0, 0, w, h)
      drawBoltOnCtx(ctx, points, color, opacity)
    }

    drawPass(1.0)
    const t1 = setTimeout(() => drawPass(0.75), LIGHTNING.PASS2_DELAY)
    const t2 = setTimeout(() => drawPass(0.4),  LIGHTNING.PASS3_DELAY)
    const t3 = setTimeout(() => {
      ctx.clearRect(0, 0, w, h)
      canvas.remove()
      this._pendingLightning = this._pendingLightning.filter(p => p.canvas !== canvas)
    }, LIGHTNING.CLEANUP_DELAY)
    this._pendingLightning.push({ canvas, timers: [t1, t2, t3] })
  },

  animateCritSpinner(tile_id, worker_id) {
    // Find the spinner layer for this tile
    const spinnerLayer = document.querySelector(`#spinner_layer_${tile_id}`)
    if (!spinnerLayer) return

    // Find the spinner matching the worker ID
    const spinner = spinnerLayer.querySelector(`.mining-spinner[data-worker-id="${worker_id}"]`)
    
    if (spinner) {
      // Store original width/height to restore later
      const originalWidth = spinner.style.width || ''
      const originalHeight = spinner.style.height || ''
      
      // Add crit-pulse class
      spinner.classList.add('crit-pulse')
      
      // Remove class and restore original size after animation completes (0.5s)
      setTimeout(() => {
        spinner.classList.remove('crit-pulse')
        // Explicitly reset width/height to ensure base styles apply
        spinner.style.width = originalWidth
        spinner.style.height = originalHeight
      }, 500)
    }
  },

  spawnDamageFloater(container, { damage, attacker_color, crit }) {
    // Check if damage floaters are enabled for current user
    const damageFloatersEnabled = document.body.getAttribute('data-damage-floaters-enabled') === 'true'
    if (!damageFloatersEnabled) {
      return // Skip spawning floater if disabled
    }
    
    // Generate random offset like the ERB template
    const offsetRatio = (Math.random() * 0.5 - 0.25).toFixed(3)
    
    // Create damage floater element
    const floater = document.createElement('div')
    floater.className = `damage-floater ${crit ? 'crit' : ''}`
    floater.style.cssText = `--offset-ratio: ${offsetRatio}; color: ${attacker_color};`
    floater.textContent = `-${damage}${crit ? '!' : ''}`
    
    // Add to container
    container.appendChild(floater)
    
    // Auto-cleanup after animation (match CSS animation duration)
    setTimeout(() => {
      if (floater.parentNode) {
        floater.remove()
      }
    }, this.DAMAGE_FLOATER_CLEANUP_DELAY)
  },

  processWorkerState(data) {
    const { worker_id, queue, coins, progress, flash_message, recursion, damage_stats, automation, promotion } = data
    
    // Update queue status
    if (queue) {
      this.updateQueueStatus(queue.queued_count, queue.max_queue_size)
    }
    
    // Update coin displays
    if (coins) {
      this.updateCoins(
        coins.deep_coins,
        coins.recursive_coins,
        coins.rc_potential,
        coins.can_upgrade,
        coins.can_auto_upgrade
      )
    }

    if (damage_stats) {
      this.updateDamageStatsDisplay(damage_stats)
    }

    if (automation && 'warn_auto_upgrade' in automation) {
      this.updateAutoUpgradeWarningState(automation.warn_auto_upgrade)
    }
    
    // Update progress display
    if (progress) {
      this.updateProgress(progress.tiles_defeated, progress.unlock_requirement)
    }
    
    // Update recursion header
    if (recursion) {
      this.updateRecursionHeader(recursion.rc_potential_formatted)
    }

    if (promotion) {
      this.updatePromotionButton(promotion)
    }
    
    // Show flash message
    if (flash_message) {
      this.showFlashMessage(flash_message.message, flash_message.amount, flash_message.type, flash_message)
    }
  },

  updateQueueStatus(queued_count, max_queue_size) {
    const element = document.getElementById('queue-status')
    if (element) {
      element.textContent = `${queued_count} / ${max_queue_size} queued`
    }
    
    // Update page title with queue status
    this.updatePageTitleWithQueueStatus(queued_count, max_queue_size)
  },

  handleIdleStarted(data) {
    const currentWorkerId = document.body?.dataset?.currentWorkerId
    if (currentWorkerId && data.worker_id && String(data.worker_id) !== String(currentWorkerId)) return

    if (window.location.pathname !== '/idle') {
      window.location.href = '/idle'
    }
  },

  hasQueueCapability() {
    const body = document.body
    return body && body.dataset.maxQueueSize && parseInt(body.dataset.maxQueueSize, 10) > 0
  },

  isAsyncDivisionActive() {
    const body = document.body
    return body?.dataset?.asyncDivision === 'true'
  },

  getBasePageTitle() {
    const body = document.body
    const baseTitle = body?.dataset?.baseTitle
    if (baseTitle && String(baseTitle).trim().length > 0) return baseTitle

    // Fallback: prefer current document title (best effort)
    return document.title || 'DeepCo™ Systems — Process. Together. Indefinitely.'
  },

  updatePageTitle({ queuedCount, maxQueueSize } = {}) {
    const queueModeEnabled = this.isQueueModeEnabled()
    const hasQueueCapability = this.hasQueueCapability()
    const inAsyncDivision = this.isAsyncDivisionActive()
    const baseTitle = this.getBasePageTitle()

    const body = document.body
    const resolvedQueuedCount = Number.isFinite(queuedCount)
      ? queuedCount
      : (body?.dataset?.queuedCount ? parseInt(body.dataset.queuedCount, 10) : 0)

    const resolvedMaxQueueSize = Number.isFinite(maxQueueSize)
      ? maxQueueSize
      : (body?.dataset?.maxQueueSize ? parseInt(body.dataset.maxQueueSize, 10) : 0)

    if (queueModeEnabled && hasQueueCapability && resolvedQueuedCount > 0 && resolvedMaxQueueSize > 0) {
      document.title = `${resolvedQueuedCount}/${resolvedMaxQueueSize} Queue - DeepCo™`
      return
    }

    if (inAsyncDivision) {
      document.title = 'ASYNC - DeepCo™'
      return
    }

    document.title = baseTitle
  },

  updatePageTitleWithQueueStatus(queued_count, max_queue_size) {
    this.updatePageTitle({ queuedCount: queued_count, maxQueueSize: max_queue_size })
  },

  initializePageTitleFromDOM() {
    // Initialize title from current DOM state
    const queueElement = document.getElementById('queue-status')
    if (queueElement && this.isQueueModeEnabled()) {
      // Parse queue status from the element text (format: "X / Y queued")
      const text = queueElement.textContent.trim()
      const match = text.match(/(\d+)\s*\/\s*(\d+)/)
      if (match) {
        const queued_count = parseInt(match[1], 10)
        const max_queue_size = parseInt(match[2], 10)
        this.updatePageTitleWithQueueStatus(queued_count, max_queue_size)
      } else {
        // Ensure we still apply Async/base title when queue parsing fails.
        this.updatePageTitle()
      }
    } else {
      // Fallback: try to get queue info from body data attributes (available on all pages)
      if (this.hasQueueCapability() && this.isQueueModeEnabled()) {
        const body = document.body
        const max_queue_size = parseInt(body.dataset.maxQueueSize, 10)
        // Get queued count from body data attribute (set on page load)
        const queued_count = body.dataset.queuedCount ? parseInt(body.dataset.queuedCount, 10) : 0
        this.updatePageTitleWithQueueStatus(queued_count, max_queue_size)
        return
      }

      // Last fallback: check DOM for queued tiles (only works on dig page)
      this.updateQueueCounterFromDOM()

      // Ensure Async/non-queue title state is applied even when queue elements are absent.
      this.updatePageTitle()
    }
  },

  updateQueueCounterFromDOM() {
    // Count queued tiles by looking at DOM elements with 'you-are-queued-here' class
    const queuedTiles = document.querySelectorAll('.you-are-queued-here').length
    const queueElement = document.getElementById('queue-status')
    if (queueElement) {
      // Get max queue size from the grid's data attribute (more reliable than parsing display text)
      const grid = document.querySelector('#grid-panel .grid[data-queue-mode]') || document.querySelector('.grid[data-queue-mode]')
      const maxQueueSize = grid ? parseInt(grid.getAttribute('data-max-queue-size')) || 50 : 50

      // Update with current count
      this.updateQueueStatus(queuedTiles, maxQueueSize)
    } else {
      // If queue element doesn't exist, still update title based on DOM state
      const grid = document.querySelector('#grid-panel .grid[data-queue-mode]') || document.querySelector('.grid[data-queue-mode]')
      if (grid) {
        const maxQueueSize = parseInt(grid.getAttribute('data-max-queue-size')) || 50
        this.updatePageTitleWithQueueStatus(queuedTiles, maxQueueSize)
      }
    }
  },

  updateCoins(deep_coins, recursive_coins, rc_potential, can_upgrade, can_auto_upgrade) {
    const coinElement = document.getElementById('worker_coins')
    if (!coinElement) return

    const dcNode = coinElement.querySelector('[data-role="dc-balance"]')
    if (dcNode) {
      dcNode.textContent = this.numberWithDelimiter(deep_coins, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    const rcNode = coinElement.querySelector('[data-role="rc-balance"]')
    if (rcNode) {
      rcNode.textContent = this.numberWithDelimiter(recursive_coins, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }

    const indicator = coinElement.querySelector('[data-role="upgrade-indicator"]')
    if (indicator) {
      indicator.classList.toggle('hidden', !can_upgrade)
    }

    this.updateAutoUpgradeHeaderBadge(!!can_auto_upgrade)
  },

  updateProgress(tiles_defeated, unlock_requirement) {
    const element = document.getElementById('tiles-defeated-badge')
    if (element) {
      element.innerHTML = `<strong class="nudge-animation" style="display: inline-block">${this.numberWithDelimiter(tiles_defeated)}/</strong><strong>${this.numberWithDelimiter(unlock_requirement)}</strong>`
    }
  },

  updateRecursionHeader(rc_potential_formatted) {
    const link = document.getElementById('recursion-header')
    if (!link) return

    const rcNode = link.querySelector('[data-role="rc-potential"]')
    if (rcNode) {
      // Set the text content, ensuring it never clears
      rcNode.textContent = rc_potential_formatted || '[ - RC]'
    }
  },

  updatePromotionButton(promotion) {
    const button = document.querySelector('[data-role="promotion-button"]')
    if (!button) return

    // Find the tooltip wrapper (span with class "tooltip")
    const tooltipWrapper = button.closest('.tooltip')
    if (!tooltipWrapper) return

    const canPromote = !!promotion.can_promote
    button.classList.toggle('btn-disabled', !canPromote)
    // Remove pointer-events-none to allow tooltips to show
    button.classList.remove('pointer-events-none')

    if (canPromote) {
      button.removeAttribute('aria-disabled')
      button.removeAttribute('tabindex')
      const titleEnabled = button.dataset.titleEnabled
      if (titleEnabled) {
        tooltipWrapper.setAttribute('data-tip', titleEnabled)
      }
    } else {
      button.setAttribute('aria-disabled', 'true')
      button.setAttribute('tabindex', '-1')
      const titleDisabled = button.dataset.titleDisabled
      if (titleDisabled) {
        tooltipWrapper.setAttribute('data-tip', titleDisabled)
      }
    }
  },

  showFlashMessage(message, amount, type, extraData = {}) {
    const notificationsEnabled = document.body?.dataset?.notificationsEnabled !== 'false'
    if (!notificationsEnabled) {
      return
    }

    const flashContainer = document.getElementById('flash-messages')
    if (!flashContainer) return

    let text
    if (message === "Achievement Unlocked:" && extraData.achievement_name) {
      text = `${message} ${extraData.achievement_name}`
    } else {
      text = amount === undefined || amount === null ? message : `${message} ${amount} [DC]`
    }

    const flash = this.buildFlashElement(text, type)
    flashContainer.insertAdjacentElement('afterbegin', flash)
    this.scheduleFlashRemoval(flash)
  },

  showGenericFlashMessage(message, type) {
    const notificationsEnabled = document.body?.dataset?.notificationsEnabled !== 'false'
    if (!notificationsEnabled) {
      return
    }

    const flashContainer = document.getElementById('flash-messages')
    if (!flashContainer) return

    const flash = this.buildFlashElement(message, type)
    flashContainer.insertAdjacentElement('afterbegin', flash)
    this.scheduleFlashRemoval(flash)
  },

  buildFlashElement(text, type) {
    const flash = document.createElement('div')
    flash.className = this.flashCardClassFor(type)
    flash.setAttribute('data-controller', 'flash')
    flash.setAttribute('role', 'alert')

    const body = document.createElement('div')
    body.className = 'card-body p-3'

    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.className = 'btn btn-ghost btn-xs absolute top-0 right-0 w-8 h-8 min-h-0 p-0 leading-none'
    closeButton.setAttribute('aria-label', 'Dismiss notification')
    closeButton.setAttribute('data-action', 'click->flash#dismissAll')
    closeButton.innerHTML = '<span class="text-xs">x</span>'

    const content = document.createElement('div')
    content.className = 'text-xs leading-snug break-words pr-5'
    content.textContent = text

    body.appendChild(closeButton)
    body.appendChild(content)
    flash.appendChild(body)

    return flash
  },

  flashCardClassFor(type) {
    const normalized = (type || 'info').toString().toLowerCase()
    const borderVariantClass = {
      notice: 'border-success',
      success: 'border-success',
      info: 'border-info',
      warning: 'border-warning',
      alert: 'border-error',
      error: 'border-error'
    }[normalized] || 'border-info'

    return `card relative shadow-md bg-base-100 border border-base-300 border-l-4 ${borderVariantClass}`
  },

  scheduleFlashRemoval(flash) {
    setTimeout(() => {
      if (flash.parentNode) {
        flash.remove()
      }
    }, this.FLASH_MESSAGE_DELAY)
  },

  showDigFlash(tile_id) {
    // Find or create dig flash element (matching the ERB template structure)
    const tileInner = document.querySelector(`#tile_inner_${tile_id}`)
    if (!tileInner) return

    let digFlash = tileInner.querySelector('.dig-flash')
    if (!digFlash) {
      digFlash = document.createElement('div')
      digFlash.className = 'dig-flash'
      digFlash.textContent = 'x'
      // Add to the tile content area at the same level as tile-icon and progress-bar
      const tileContentDiv = tileInner.querySelector('.tile > div')
      if (tileContentDiv) {
        tileContentDiv.appendChild(digFlash)
      }
    }

    // Force show the dig flash with inline style
    digFlash.style.display = 'block'
    digFlash.style.visibility = 'visible'

    // Hide after animation
    setTimeout(() => {
      digFlash.style.display = 'none'
    }, this.DIG_FLASH_DURATION)
  },

  numberWithDelimiter(number, options = {}) {
    const value = Number(number ?? 0)
    if (Number.isNaN(value)) return '0'

    const defaults = { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    return value.toLocaleString(undefined, { ...defaults, ...options })
  },

  isQueueModeEnabled() {
    // Check if queue mode is enabled by looking for the data attribute on the body (available on all pages)
    // or fall back to grid element (for backward compatibility)
    const body = document.body
    if (body && body.dataset.queueMode) {
      const queueMode = body.dataset.queueMode
      return queueMode === 'sequential' || queueMode === 'random'
    }
    
    // Fallback: check grid element (for backward compatibility)
    const grid = document.querySelector('#grid-panel .grid[data-queue-mode]') || document.querySelector('.grid[data-queue-mode]')
    if (grid) {
      const queueMode = grid.getAttribute('data-queue-mode')
      return queueMode === 'sequential' || queueMode === 'random'
    }
    
    return false
  },

  // Error handling methods
  handleProcessingError(error, data) {
    // Log error but don't break the connection
    console.warn("[GameUpdatesChannel] Recovering from processing error:", error.message)
    this.pendingGridStateRefresh = true
    this.scheduleGridStateRetry()
  },

  resetGridStateTracking() {
    this.gridStateLoaded = false
    this.pendingGridStateRefresh = true
    this.gridStateRetryTimeout = null
    this.gridStateController = null
    this.isFetchingGridState = false
    this.pendingDomMessages = []
  },

  ensureGridState({ force = false, reason = 'unknown' } = {}) {
    const gridFrame = this.gridFrameElement()
    if (!gridFrame) {
      this.pendingGridStateRefresh = false
      return
    }

    if (this.isFetchingGridState) {
      return
    }

    const shouldFetch = force || !this.gridStateLoaded || this.pendingGridStateRefresh
    if (!shouldFetch) {
      return
    }

    this.pendingGridStateRefresh = false
    this.fetchGridState({ reason })
  },

  fetchGridState({ reason = 'manual' } = {}) {
    const url = this.gridStateUrl()
    if (!url) {
      this.pendingGridStateRefresh = false
      this.gridStateLoaded = true
      return
    }

    this.clearGridStateRetry()

    if (this.gridStateController && typeof this.gridStateController.abort === 'function') {
      this.gridStateController.abort()
    }

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    this.gridStateController = controller
    this.isFetchingGridState = true

    const fetchOptions = {
      headers: { Accept: 'text/vnd.turbo-stream.html' },
      credentials: 'same-origin'
    }

    if (controller) {
      fetchOptions.signal = controller.signal
    }

    fetch(url, fetchOptions)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        if (!html) {
          return
        }

        const trimmed = html.trim()
        if (!trimmed) {
          return
        }

        if (window.Turbo && typeof window.Turbo.renderStreamMessage === 'function') {
          window.Turbo.renderStreamMessage(trimmed)
          window.requestAnimationFrame(() => this.handleGridStateLoaded())
        } else {
          this.replaceGridPanel(trimmed)
          this.handleGridStateLoaded()
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          return
        }
        console.warn(`[GameUpdatesChannel] Grid state fetch failed (${reason}):`, error)
        this.pendingGridStateRefresh = true
        this.scheduleGridStateRetry()
      })
      .finally(() => {
        if (controller && this.gridStateController === controller) {
          this.gridStateController = null
        }
        this.isFetchingGridState = false
      })
  },

  gridStateUrl() {
    const frame = this.gridFrameElement()
    if (!frame) {
      return null
    }

    const src = frame.getAttribute('src')
    if (src) {
      return src
    }

    const body = typeof document !== 'undefined' ? document.body : null
    if (body && body.dataset && body.dataset.gridStateUrl) {
      return body.dataset.gridStateUrl
    }

    return null
  },

  replaceGridPanel(html) {
    const template = document.createElement('template')
    template.innerHTML = html
    const replacement = template.content.querySelector('turbo-frame#grid-panel') || template.content.firstElementChild
    const target = document.getElementById('grid-panel')

    if (replacement && target) {
      target.replaceWith(replacement)
    } else if (target) {
      target.innerHTML = html
    }
  },

  handleGridStateLoaded() {
    this.gridStateLoaded = true
    this.pendingGridStateRefresh = false
    this.flushDeferredDomMessages()
    this.cleanOrphanedSpinners()
    // Initialize page title with current queue status after grid loads
    this.initializePageTitleFromDOM()
  },

  scheduleGridStateRetry() {
    if (this.gridStateRetryTimeout) {
      return
    }

    this.gridStateRetryTimeout = setTimeout(() => {
      this.gridStateRetryTimeout = null
      this.ensureGridState({ force: true, reason: 'retry' })
    }, this.GRID_STATE_RETRY_DELAY)
  },

  clearGridStateRetry() {
    if (this.gridStateRetryTimeout) {
      clearTimeout(this.gridStateRetryTimeout)
      this.gridStateRetryTimeout = null
    }
  },

  deferDomMessage(data) {
    if (!this.pendingDomMessages) {
      this.pendingDomMessages = []
    }
    this.pendingDomMessages.push(data)
  },

  flushDeferredDomMessages() {
    if (!this.pendingDomMessages || this.pendingDomMessages.length === 0) {
      return
    }

    const messages = this.pendingDomMessages.slice()
    this.pendingDomMessages.length = 0
    messages.forEach(message => {
      try {
        this.dispatchDomMessage(message)
      } catch (error) {
        console.warn("[GameUpdatesChannel] Failed deferred message:", error, message)
      }
    })
  },

  shouldDeferMessage(data) {
    return data && this.DOM_DEPENDENT_MESSAGE_TYPES.has(data.type)
  },

  cleanOrphanedSpinners(tile_id) {
    const collectContainers = () => {
      if (tile_id !== undefined && tile_id !== null) {
        const container = document.getElementById(`spinner_layer_${tile_id}`)
        return container ? [ container ] : []
      }
      return Array.from(document.querySelectorAll('[id^="spinner_layer_"]'))
    }

    collectContainers().forEach(container => {
      const match = container.id.match(/^spinner_layer_(\d+)$/)
      const relatedTileId = match ? match[1] : null
      const tileInner = relatedTileId ? document.getElementById(`tile_inner_${relatedTileId}`) : null

      if (!tileInner || !tileInner.querySelector('.tile')) {
        container.innerHTML = ''
      }
    })
  },

  handleTileDeath(tile_id) {
    // Prevent duplicate calls for the same tile
    if (this.deadTiles && this.deadTiles.has(tile_id)) {
      return
    }
    if (!this.deadTiles) {
      this.deadTiles = new Set()
    }
    this.deadTiles.add(tile_id)

    // Remove spinners
    const spinnerContainer = document.getElementById(`spinner_layer_${tile_id}`)
    if (spinnerContainer) {
      spinnerContainer.innerHTML = ''
    }

    // Remove mining/queue indicators
    const tileInner = document.getElementById(`tile_inner_${tile_id}`)
    if (tileInner) {
      tileInner.classList.remove('you-are-mining-here')
      tileInner.classList.remove('you-are-queued-here')
    }

    // Hide progress bar (already handled by updateTileHealth when dug=true, but ensure it's hidden)
    const progressBar = document.querySelector(`#tile_inner_${tile_id} .progress-bar`)
    if (progressBar) {
      progressBar.style.width = '0%'
      progressBar.style.display = 'none'
    }

    // Always hide tile content immediately. If it later turns out the tile had a reward,
    // the server will send `tile_cleanup`, which will "revive" the content and swap in
    // the correct reward state (junk/valuable).
    const tileElement = document.querySelector(`#tile_inner_${tile_id} .tile > div`)
    if (tileElement) {
      const tileContainer = tileElement.closest('.tile')
      if (tileContainer) {
        tileContainer.classList.remove('undug')
      }
      tileElement.classList.remove('undug')
      // If reward cleanup already arrived (can happen before the delayed damage_batch),
      // don't hide it again.
      const hasReward = tileElement.classList.contains('junk') || tileElement.classList.contains('valuable')
      if (!hasReward) {
        tileElement.style.visibility = 'hidden'
        tileElement.style.pointerEvents = 'none'
      }
    }

    // Show death animation (the "x" flash) for all tiles
    this.showDigFlash(tile_id)
  },

  applyTileCleanup(data) {
    const { tile_id, newly_dug, tile_result, tile_icon } = data || {}

    // Fallback: handle spinner cleanup in case WS arrives before damage_batch
    const spinnerContainer = document.getElementById(`spinner_layer_${tile_id}`)
    if (spinnerContainer) {
      spinnerContainer.innerHTML = ''
    }

    // Only handle reward display (cleanup and death animation are done by handleTileDeath when frontend detects death)
    if (newly_dug && tile_result && tile_result !== 'dirt') {
      const tileElement = document.querySelector(`#tile_inner_${tile_id} .tile > div`)
      if (tileElement) {
        const tileContainer = tileElement.closest('.tile')
        if (tileContainer) {
          tileContainer.classList.remove('undug')
        }
        tileElement.style.visibility = 'visible'
        tileElement.style.pointerEvents = 'auto'
        tileElement.classList.remove('undug')
        tileElement.classList.remove('dirt', 'junk', 'valuable')
        tileElement.classList.add(tile_result)

        const iconElement = tileElement.querySelector('.tile-icon')
        if (iconElement && tile_icon) {
          iconElement.textContent = tile_icon
        }
      }
    }

    return true
  },

  gridFrameElement() {
    if (typeof document === 'undefined') return null
    return document.getElementById('grid-panel')
  },

  processAutoClaimResult(data) {
    const { success, message, claimed_count, total_dc_reward, new_deep_coins, has_claimable_achievements } = data

    // Show flash message using a generic method
    this.showGenericFlashMessage(message, success ? 'success' : 'error')

    if (!success) return;
    // Update coin display if we got rewards
    if (new_deep_coins !== undefined) {
      this.updateCoinsFromAutoAction(new_deep_coins)
    }

    // Always update achievement header badge highlighting after auto claim attempt
    this.updateAchievementHeaderBadge(has_claimable_achievements)
  },

  processAutoUpgradeResult(data) {
    const { success, message, total_upgrades, new_deep_coins, has_upgradable_upgrades, damage_stats, efficiency_data, requires_auto_upgrade_confirmation } = data

    // Show flash message using a generic method
    this.showGenericFlashMessage(message, success ? 'success' : 'error')

    if (!success) return;
    // Update coin display
    if (new_deep_coins !== undefined) {
      this.updateCoinsFromAutoAction(new_deep_coins)
    }

    // Always update auto upgrade header badge highlighting after auto upgrade attempt
    this.updateAutoUpgradeHeaderBadge(has_upgradable_upgrades)

    // Update damage stats display if we have new stats
    if (damage_stats) {
      this.updateDamageStatsDisplay(damage_stats)
    }

    // Update efficiency bar if we have new efficiency data
    if (efficiency_data) {
      this.updateEfficiencyBar(efficiency_data)
    }

    if (requires_auto_upgrade_confirmation !== undefined) {
      this.updateAutoUpgradeWarningState(requires_auto_upgrade_confirmation)
    }
  },

  updateCoinsFromAutoAction(new_deep_coins) {
    // Update the coin display when automation spends or earns DC
    const coinElement = document.getElementById('worker_coins')
    if (!coinElement) return;

    const dcNode = coinElement.querySelector('[data-role="dc-balance"]')
    if (dcNode) {
      dcNode.textContent = this.numberWithDelimiter(new_deep_coins, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
  },

  updateAchievementHeaderBadge(has_claimable_achievements) {
    const achievementHeader = document.getElementById('achievement-header')
    if (achievementHeader) {
      this.updateIndicatorVisibility(achievementHeader, 'achievement-indicator', has_claimable_achievements)

      const achievementLink = achievementHeader.querySelector('a')
      if (achievementLink) {
        achievementLink.classList.toggle('badge', has_claimable_achievements)
      }
    }

    const autoClaimContainer = document.getElementById('auto-claim-header')
    if (autoClaimContainer) {
      this.updateIndicatorVisibility(autoClaimContainer, 'auto-claim-indicator', has_claimable_achievements)

      const autoClaimButton = autoClaimContainer.querySelector('.auto-claim')
      if (autoClaimButton) {
        autoClaimButton.classList.toggle('badge', has_claimable_achievements)
      }
    }
  },

  updateAutoUpgradeHeaderBadge(has_upgradable_upgrades) {
    const autoUpgradeContainer = document.getElementById('auto-upgrade-header')
    if (!autoUpgradeContainer) return

    this.updateIndicatorVisibility(autoUpgradeContainer, 'auto-upgrade-indicator', has_upgradable_upgrades)

    const autoUpgradeButton = autoUpgradeContainer.querySelector('.auto-upgrade')
    if (!autoUpgradeButton) return

    autoUpgradeButton.classList.toggle('badge', has_upgradable_upgrades)
  },

  updateAutoUpgradeWarningState(shouldWarn) {
    const autoUpgradeContainer = document.getElementById('auto-upgrade-header')
    if (!autoUpgradeContainer) return

    const autoUpgradeButton = autoUpgradeContainer.querySelector('.auto-upgrade')
    if (!autoUpgradeButton) return

    const form = autoUpgradeButton.closest('form')
    const warn = shouldWarn === true || shouldWarn === 'true'
    const warningMessage = autoUpgradeContainer.dataset.warningMessage
    const hiddenFieldName = 'force_out_of_normal_range'
    let forceField = form ? form.querySelector(`input[name="${hiddenFieldName}"]`) : null

    if (warn) {
      if (warningMessage) {
        autoUpgradeButton.dataset.turboConfirm = warningMessage
      }

      if (!forceField && form) {
        forceField = document.createElement('input')
        forceField.type = 'hidden'
        forceField.name = hiddenFieldName
        forceField.value = 'true'
        form.appendChild(forceField)
      } else if (forceField) {
        forceField.value = 'true'
      }
    } else {
      delete autoUpgradeButton.dataset.turboConfirm

      if (forceField) {
        forceField.value = 'false'
      }
    }
  },

  updateDamageStatsDisplay(damage_stats) {
    const containers = document.querySelectorAll('[data-component="damage-stats"]')
    if (!containers.length) return

    containers.forEach(container => {
      const setTextContent = (stat, value) => {
        const nodes = container.querySelectorAll(`[data-stat="${stat}"]`)
        if (!nodes.length) return
        nodes.forEach(node => {
          node.textContent = value
        })
      }

      const updateNumericStat = (stat, numericValue, { defaultPrecision = 2 } = {}) => {
        if (!Number.isFinite(numericValue)) return
        const nodes = container.querySelectorAll(`[data-stat="${stat}"]`)
        if (!nodes.length) return

        nodes.forEach(node => {
          const precisionAttr = node.dataset.precision
          const precision = precisionAttr !== undefined ? Number(precisionAttr) : defaultPrecision
          const hasPrecision = Number.isFinite(precision)
          const format = node.dataset.format
          let formatted

          if (format === 'delimited') {
            const formattingPrecision = hasPrecision ? precision : defaultPrecision
            formatted = this.numberWithDelimiter(numericValue, {
              minimumFractionDigits: formattingPrecision,
              maximumFractionDigits: formattingPrecision
            })
          } else if (hasPrecision) {
            formatted = numericValue.toFixed(precision)
          } else {
            formatted = numericValue.toString()
          }

          node.textContent = formatted
        })
      }

      if ('damage_rating' in damage_stats) {
        const rating = Number(damage_stats.damage_rating)
        if (Number.isFinite(rating)) {
          updateNumericStat('damage_rating', rating)
        } else if (damage_stats.damage_rating !== undefined) {
          setTextContent('damage_rating', damage_stats.damage_rating)
        }
      }

      if ('crit_chance' in damage_stats) {
        setTextContent('crit_chance', `${damage_stats.crit_chance}%`)
      }

      if ('chain_chance' in damage_stats) {
        setTextContent('chain_chance', `${damage_stats.chain_chance}%`)
      }

      if ('min_damage' in damage_stats) {
        setTextContent('min_damage', this.numberWithDelimiter(damage_stats.min_damage))
      }

      if ('max_damage' in damage_stats) {
        setTextContent('max_damage', this.numberWithDelimiter(damage_stats.max_damage))
      }

      if ('dig_cooldown' in damage_stats) {
        const cooldown = Number(damage_stats.dig_cooldown)
        if (Number.isFinite(cooldown)) {
          setTextContent('dig_cooldown', `${cooldown.toFixed(2)}s`)
        } else if (damage_stats.dig_cooldown !== undefined) {
          setTextContent('dig_cooldown', `${damage_stats.dig_cooldown}s`)
        }
      }
    })
  },

  updateEfficiencyBar(efficiency_data = {}) {
    const wrapper = document.querySelector('[data-role="department-efficiency"]')
    if (!wrapper) return

    const labelNode = wrapper.querySelector('[data-role="department-efficiency-label"]')
    if (!labelNode) return

    const { css_class, status_label, status_key, category } = efficiency_data

    const statusClass = css_class
    if (statusClass) {
      TEXT_VARIANT_CLASSES.forEach(cls => labelNode.classList.remove(cls))
      labelNode.classList.add(statusClass)

      const efficiencyContainer = wrapper.closest('[data-role="department-efficiency-container"]')
      if (efficiencyContainer) {
        const ratingNodes = efficiencyContainer.querySelectorAll('[data-role="department-efficiency-rating"]')
        ratingNodes.forEach(node => {
          TEXT_VARIANT_CLASSES.forEach(cls => node.classList.remove(cls))
          node.classList.add(statusClass)
        })
      }
    }

    const effectiveStatusKey = status_key || this.statusKeyFromCssClass(statusClass) || this.statusKeyFromCategory(category)
    if (effectiveStatusKey) {
      wrapper.dataset.statusKey = effectiveStatusKey
    }

    const resolvedLabel = status_label || EFFICIENCY_STATUS_LABELS[effectiveStatusKey] || titleize(category) || titleize(effectiveStatusKey)
    if (resolvedLabel) {
      wrapper.dataset.statusLabel = resolvedLabel
    }

    const prRange = wrapper.dataset.prRange
    const tipParts = []
    if (resolvedLabel) tipParts.push(resolvedLabel)
    if (prRange) tipParts.push(prRange)
    if (tipParts.length) {
      wrapper.setAttribute('data-tip', tipParts.join(', '))
    }
  },

  statusKeyFromCssClass(cssClass) {
    if (!cssClass) return null
    const match = cssClass.match(/text-([\w-]+)/)
    return match ? match[1] : null
  },

  statusKeyFromCategory(category) {
    if (!category) return null
    return String(category).toLowerCase()
  },

  resolveElement(target) {
    if (!target) return null
    if (typeof target === 'string') {
      return document.querySelector(target)
    }
    return target
  },

  updateIndicatorVisibility(containerOrSelector, indicatorRole, shouldShow) {
    const container = this.resolveElement(containerOrSelector)
    if (!container) return

    let indicator = indicatorRole
      ? container.querySelector(`[data-role="${indicatorRole}"]`)
      : container.querySelector('.indicator-item')

    if (!indicator && shouldShow) {
      indicator = document.createElement('span')
      indicator.className = 'indicator-item indicator status status-info'
      if (indicatorRole) {
        indicator.dataset.role = indicatorRole
      }
      container.prepend(indicator)
    }

    if (indicator) {
      indicator.classList.toggle('hidden', !shouldShow)
    }
  },

  DOM_DEPENDENT_MESSAGE_TYPES: new Set([
    'damage_batch',
    'fork_batch',
    'tile_assignment',
    'personal_queue_status',
    'personal_queue_status_batch',
    'tile_cleanup'
  ]),

  // Constants
  GRID_STATE_RETRY_DELAY: 2000,
  DAMAGE_FLOATER_CLEANUP_DELAY: 2000,
  FLASH_MESSAGE_DELAY: 3000,
  DIG_FLASH_DURATION: 1000,

})

// Expose globally for debugging
window.gameUpdatesChannel = gameUpdatesChannel;

// Initialize page title on DOM ready (in case ActionCable hasn't connected yet)
if (typeof document !== 'undefined') {
  const initializeTitle = () => {
    if (gameUpdatesChannel && typeof gameUpdatesChannel.initializePageTitleFromDOM === 'function') {
      gameUpdatesChannel.initializePageTitleFromDOM()
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTitle)
  } else {
    // DOM already loaded, initialize immediately
    initializeTitle()
  }
  
  // Re-initialize page title after Turbo navigation (prevents title from being overwritten by HTML title tag)
  // Ensure only one listener is registered, even if this module is re-evaluated
  if (window._turboLoadTitleListener) {
    document.removeEventListener('turbo:load', window._turboLoadTitleListener)
  }
  window._turboLoadTitleListener = initializeTitle
  document.addEventListener('turbo:load', window._turboLoadTitleListener)
}
